import { ShieldCheck } from "lucide-react";
import {
  useCreate,
  useGetList,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useState } from "react";
import { Input } from "@/components/ui/input";

import { AsideSection } from "../../atomic-crm/misc/AsideSection";
import { GDPR_ITEMS } from "../common/constants";
import { ProgressBar } from "../common/components";

type Items = Record<string, { done?: boolean; date?: string }>;

/** GDPR compliance checklist of a client company (health data in clinics). */
export const GdprChecklist = ({ companyId }: { companyId: Identifier }) => {
  const translate = useTranslate();
  const [create] = useCreate();
  const [update] = useUpdate();
  const { data, refetch } = useGetList("gdpr_checklists", {
    filter: { company_id: companyId },
    pagination: { page: 1, perPage: 1 },
  });
  const checklist = data?.[0];
  const items: Items = checklist?.items ?? {};
  const [region, setRegion] = useState<string | null>(null);
  const done = GDPR_ITEMS.filter((key) => items[key]?.done).length;

  const save = (patch: { items?: Items; data_region?: string | null }) => {
    if (checklist) {
      update(
        "gdpr_checklists",
        { id: checklist.id, data: patch, previousData: checklist },
        { mutationMode: "pessimistic", onSuccess: () => refetch() },
      );
    } else {
      create(
        "gdpr_checklists",
        { data: { company_id: companyId, items: {}, ...patch } },
        { onSuccess: () => refetch() },
      );
    }
  };

  const toggle = (key: string) =>
    save({
      items: {
        ...items,
        [key]: items[key]?.done
          ? { done: false }
          : { done: true, date: new Date().toISOString().slice(0, 10) },
      },
    });

  return (
    <AsideSection title={translate("hub.gdpr.title")}>
      <div className="w-full space-y-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="size-4" />
          {translate("hub.gdpr.progress", {
            done,
            total: GDPR_ITEMS.length,
          })}
        </div>
        <ProgressBar
          value={(100 * done) / GDPR_ITEMS.length}
          showLabel={false}
        />
        <ul className="space-y-1">
          {GDPR_ITEMS.map((key) => (
            <li key={key}>
              <label className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 accent-primary"
                  checked={!!items[key]?.done}
                  onChange={() => toggle(key)}
                />
                <span
                  className={items[key]?.done ? "text-muted-foreground" : ""}
                >
                  {translate(`hub.gdpr.items.${key}`)}
                  {items[key]?.date && (
                    <span className="ml-1 text-xs text-muted-foreground">
                      ({items[key].date})
                    </span>
                  )}
                </span>
              </label>
            </li>
          ))}
        </ul>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">
            {translate("hub.gdpr.region")}
          </p>
          <Input
            className="h-8"
            placeholder="eu-west-1 (Irlanda)"
            value={region ?? checklist?.data_region ?? ""}
            onChange={(e) => setRegion(e.target.value)}
            onBlur={() =>
              region !== null &&
              region !== (checklist?.data_region ?? "") &&
              save({ data_region: region || null })
            }
          />
        </div>
      </div>
    </AsideSection>
  );
};
